import { useState, useEffect } from "react";
import { postData, serverURL } from "../../../../Services/Fetchnodeservices";
import { useLocation, useNavigate } from "react-router-dom";

// Icons
const SpinnerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" style={{ animation: "spin 0.8s linear infinite", transformOrigin: "center" }}/>
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const LocationIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const PhoneIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const OrderStatusBadge = ({ status, theme }) => {
  const statusMap = {
    delivered: { bg: "rgba(16,185,129,0.12)", color: "#10b981", text: "Delivered" },
    completed: { bg: "rgba(16,185,129,0.12)", color: "#10b981", text: "Completed" },
    pending: { bg: "rgba(245,158,11,0.12)", color: "#f59e0b", text: "Pending" },
    placed: { bg: "rgba(59,130,246,0.12)", color: "#3b82f6", text: "Placed" },
    accepted: { bg: "rgba(139,92,246,0.12)", color: "#8b5cf6", text: "Accepted" },
    processing: { bg: "rgba(59,130,246,0.12)", color: "#3b82f6", text: "Processing" },
    cancelled: { bg: "rgba(239,68,68,0.12)", color: "#ef4444", text: "Cancelled" },
    confirmed: { bg: "rgba(139,92,246,0.12)", color: "#8b5cf6", text: "Confirmed" },
    shipped: { bg: "rgba(6,182,212,0.12)", color: "#06b6d4", text: "Shipped" }
  };
  const c = statusMap[status?.toLowerCase()] || { bg: "rgba(107,114,128,0.12)", color: "#6b7280", text: status || "Unknown" };
  return (
    <span style={{ fontSize: "11px", fontWeight: 600, padding: "4px 10px", borderRadius: "12px", background: c.bg, color: c.color }}>
      {c.text}
    </span>
  );
};

const OrderHistory = ({ onClose, isDark = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const user = location.state?.user;
  
  console.log("Received user:", user);
  
  const theme = isDark ? 'dark' : 'light';
  
  const colors = {
    background: isDark ? "#0d1117" : "#f9fafb",
    surface: isDark ? "#141824" : "#ffffff",
    border: isDark ? "#1e2740" : "#e5e7eb",
    borderLight: isDark ? "#1e2740" : "#f3f4f6",
    textPrimary: isDark ? "#f1f5f9" : "#111827",
    textSecondary: isDark ? "#94a3b8" : "#6b7280",
    textMuted: isDark ? "#64748b" : "#9ca3af",
    inputBg: isDark ? "#141824" : "#f9fafb",
  };

  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    if (user?.id) {
      fetchOrders();
    } else {
      console.error("No user ID found");
      setError("User information not found");
      setLoading(false);
    }
  }, [user?.id]);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log("📦 Fetching orders for user ID:", user?.id);
      
      const response = await postData("users/ordersbyid", { userid: user?.id });
      console.log("📦 Full API Response:", response);
      
      let ordersData = [];
      
      if (!response) {
        console.error("No response received from API");
        setError("No response from server");
        setLoading(false);
        return;
      }
      
      // Try different response formats
      if (response.status === true && response.data) {
        if (Array.isArray(response.data)) {
          ordersData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          ordersData = response.data.data;
        }
      } else if (Array.isArray(response)) {
        ordersData = response;
      } else if (response.data && Array.isArray(response.data)) {
        ordersData = response.data;
      } else if (response.result && Array.isArray(response.result)) {
        ordersData = response.result;
      } else if (response.orders && Array.isArray(response.orders)) {
        ordersData = response.orders;
      } else if (response.items && Array.isArray(response.items)) {
        ordersData = response.items;
      } else if (response.response && Array.isArray(response.response)) {
        ordersData = response.response;
      }
      
      console.log("📦 Extracted orders count:", ordersData.length);
      console.log("📦 First order sample:", ordersData[0]);
      
      // ✅ Sort orders in decreasing order (newest first)
      ordersData = ordersData.sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });
      
      setAllOrders(ordersData);
      setOrders(ordersData);
      
    } catch (err) {
      console.error("❌ Error fetching orders:", err);
      setError(err.message || "Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (onClose && typeof onClose === 'function') {
      onClose();
      return;
    }
    navigate(-1);
  };

  const filterOrders = (type) => {
    setActiveTab(type);
    
    if (type === "all") {
      setOrders(allOrders);
    } else if (type === "delivered") {
      setOrders(allOrders.filter(o => 
        o.order_status_user?.toLowerCase() === "delivered" || 
        o.order_status_driver?.toLowerCase() === "delivered"
      ));
    } else if (type === "pending") {
      setOrders(allOrders.filter(o => {
        const status = (o.order_status_user || o.order_status_restro || "").toLowerCase();
        return status === "pending" || status === "processing" || status === "confirmed" || status === "placed" || status === "accepted";
      }));
    } else if (type === "cancelled") {
      setOrders(allOrders.filter(o => 
        o.order_status_user?.toLowerCase() === "cancelled" || 
        o.order_status_restro?.toLowerCase() === "cancelled"
      ));
    }
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getItemsList = (itemsStr) => {
    try {
      const items = JSON.parse(itemsStr);
      if (Array.isArray(items)) {
        return items.map(item => `${item.name} x${item.qty}`).join(", ");
      }
      return itemsStr;
    } catch {
      return itemsStr || "-";
    }
  };

  const getItemsArray = (itemsStr) => {
    try {
      const items = JSON.parse(itemsStr);
      if (Array.isArray(items)) {
        return items;
      }
      return [];
    } catch {
      return [];
    }
  };

  // ✅ Function to get restaurant name from various possible fields
  const getRestaurantName = (order) => {
    if (order.organisation_name) return order.organisation_name;
    if (order.designation) return order.designation;
    if (order.restaurant_name) return order.restaurant_name;
    if (order.business_name) return order.business_name;
    return "N/A";
  };

  // ✅ Function to get rider name from driver_name field
  const getRiderName = (order) => {
    if (order.driver_name) return order.driver_name;
    if (order.rider_name) return order.rider_name;
    return "Not assigned yet";
  };

  // ✅ Function to get image URL with correct path using serverURL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath === "" || imagePath === "null" || imagePath === "NULL") return null;
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    
    // Extract filename from path
    let filename = imagePath;
    if (filename.includes('/')) {
      filename = filename.split('/').pop();
    }
    if (filename.includes('\\')) {
      filename = filename.split('\\').pop();
    }
    
    // Construct the full URL using serverURL
    return `${serverURL}/public/uploadnew/upload/${filename}`;
  };

  const stats = {
    total: allOrders.length,
    delivered: allOrders.filter(o => 
      o.order_status_user?.toLowerCase() === "delivered" || 
      o.order_status_driver?.toLowerCase() === "delivered"
    ).length,
    pending: allOrders.filter(o => {
      const status = (o.order_status_user || o.order_status_restro || "").toLowerCase();
      return status === "pending" || status === "processing" || status === "confirmed" || status === "placed" || status === "accepted";
    }).length,
    cancelled: allOrders.filter(o => 
      o.order_status_user?.toLowerCase() === "cancelled" || 
      o.order_status_restro?.toLowerCase() === "cancelled"
    ).length,
    totalSpent: allOrders.reduce((sum, order) => {
      const amount = parseFloat(order.final_amount) || parseFloat(order.amount) || 0;
      return sum + amount;
    }, 0)
  };

  // ✅ FULL PAGE ORDER DETAIL VIEW
  const OrderDetailPage = ({ order, onClose: modalClose }) => {
    const restaurantName = getRestaurantName(order);
    const riderName = getRiderName(order);
    
    // ✅ Get image URLs with correct path
    const pickupImage = getImageUrl(order.delivery_pickup_pic);
    const dropoffImage = getImageUrl(order.delivery_dropoff_pic);
    
    return (
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: colors.background,
        zIndex: 1000,
        overflow: "auto",
        padding: "20px 24px",
        animation: "slideIn 0.3s ease"
      }}>
        <style>{`
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}</style>

        {/* Header with Back Button */}
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: "16px", 
          marginBottom: "24px",
          flexWrap: "wrap",
          position: "sticky",
          top: 0,
          background: colors.background,
          zIndex: 10,
          padding: "10px 0"
        }}>
          <button 
            onClick={modalClose}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: "10px",
              cursor: "pointer",
              color: colors.textSecondary,
              fontSize: "13px",
              fontWeight: 500,
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isDark ? "#1e2740" : "#f3f4f6";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = colors.surface;
            }}
          >
            <ChevronLeftIcon />
            Back
          </button>
          
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0, fontSize: "24px", fontWeight: 700, color: colors.textPrimary }}>
              Order Details #{order.id}
            </h1>
            <p style={{ margin: "8px 0 0", fontSize: "14px", color: colors.textMuted }}>
              {formatDateTime(order.created_at)}
            </p>
          </div>
        </div>

        {/* Order Details Content */}
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          {/* Order Info */}
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
            gap: "16px", 
            marginBottom: "20px",
            background: colors.surface,
            padding: "20px",
            borderRadius: "12px",
            border: `1px solid ${colors.border}`
          }}>
            <div>
              <strong style={{ color: colors.textPrimary }}>Order ID:</strong>
              <p style={{ margin: "4px 0 0", color: colors.textSecondary, fontFamily: "monospace" }}>#{order.id}</p>
            </div>
            <div>
              <strong style={{ color: colors.textPrimary }}>Date & Time:</strong>
              <p style={{ margin: "4px 0 0", color: colors.textSecondary }}>{formatDateTime(order.created_at)}</p>
            </div>
            <div>
              <strong style={{ color: colors.textPrimary }}>Payment Method:</strong>
              <p style={{ margin: "4px 0 0", color: colors.textSecondary }}>{order.pay_method || "N/A"}</p>
            </div>
            <div>
              <strong style={{ color: colors.textPrimary }}>Payment Status:</strong>
              <p style={{ 
                margin: "4px 0 0", 
                color: order.pay_status === "paid" ? "#10b981" : "#f59e0b",
                fontWeight: 600
              }}>
                {order.pay_status === "paid" ? "✓ Paid" : "Pending"}
              </p>
            </div>
          </div>

          {/* Restaurant Info */}
          <div style={{ 
            marginBottom: "20px",
            background: colors.surface,
            padding: "20px",
            borderRadius: "12px",
            border: `1px solid ${colors.border}`
          }}>
            <strong style={{ color: colors.textPrimary, fontSize: "16px" }}>Restaurant:</strong>
            <p style={{ margin: "8px 0 0", color: colors.textSecondary, fontSize: "16px", fontWeight: 500 }}>
              {restaurantName}
            </p>
          </div>

          {/* Rider Info */}
          <div style={{ 
            marginBottom: "20px",
            background: colors.surface,
            padding: "20px",
            borderRadius: "12px",
            border: `1px solid ${colors.border}`
          }}>
            <strong style={{ color: colors.textPrimary, fontSize: "16px" }}>Rider:</strong>
            <p style={{ margin: "8px 0 0", color: colors.textSecondary, fontSize: "16px", fontWeight: 500 }}>
              {riderName}
            </p>
          </div>

          {/* Items Details */}
          <div style={{ marginBottom: "20px" }}>
            <strong style={{ color: colors.textPrimary, fontSize: "16px" }}>Items Ordered:</strong>
            <div style={{ 
              marginTop: "8px", 
              background: colors.surface,
              padding: "16px", 
              borderRadius: "12px",
              border: `1px solid ${colors.border}`
            }}>
              {getItemsArray(order.items).map((item, idx) => (
                <div key={idx} style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  marginBottom: idx < getItemsArray(order.items).length - 1 ? "12px" : 0,
                  paddingBottom: idx < getItemsArray(order.items).length - 1 ? "12px" : 0,
                  borderBottom: idx < getItemsArray(order.items).length - 1 ? `1px solid ${colors.border}` : "none"
                }}>
                  <div>
                    <span style={{ color: colors.textPrimary, fontWeight: 500 }}>{item.name}</span>
                    {item.variant_name && (
                      <span style={{ color: colors.textMuted, fontSize: "11px", marginLeft: "8px" }}>({item.variant_name})</span>
                    )}
                    <div style={{ color: colors.textMuted, fontSize: "11px", marginTop: "4px" }}>
                      Qty: {item.qty} × ₹{item.price}
                    </div>
                    {item.addons && item.addons.length > 0 && (
                      <div style={{ color: colors.textMuted, fontSize: "10px", marginTop: "4px" }}>
                        Addons: {item.addons.map(a => a.name).join(", ")}
                      </div>
                    )}
                  </div>
                  <span style={{ color: colors.textPrimary, fontWeight: 600 }}>₹{item.price * item.qty}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Price Breakdown */}
          <div style={{ marginBottom: "20px" }}>
            <strong style={{ color: colors.textPrimary, fontSize: "16px" }}>Price Breakdown:</strong>
            <div style={{ marginTop: "8px", background: colors.surface, padding: "20px", borderRadius: "12px", border: `1px solid ${colors.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ color: colors.textSecondary }}>Item Total:</span>
                <span style={{ color: colors.textPrimary }}>₹{order.price || 0}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ color: colors.textSecondary }}>Delivery Charge:</span>
                <span style={{ color: colors.textPrimary }}>₹{order.delivery_charge || 0}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ color: colors.textSecondary }}>Tax:</span>
                <span style={{ color: colors.textPrimary }}>₹{order.tax || 0}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ color: colors.textSecondary }}>Platform Fee:</span>
                <span style={{ color: colors.textPrimary }}>₹{order.user_platform_fee || order.business_platform_fee || 0}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ color: colors.textSecondary }}>Handling Charge:</span>
                <span style={{ color: colors.textPrimary }}>₹{order.handling_charge || 0}</span>
              </div>
              {order.discount > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                  <span style={{ color: colors.textSecondary }}>Discount:</span>
                  <span style={{ color: "#10b981" }}>-₹{order.discount}</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "12px", marginTop: "8px", borderTop: `1px solid ${colors.border}` }}>
                <strong style={{ color: colors.textPrimary, fontSize: "16px" }}>Total Amount:</strong>
                <strong style={{ color: "#10b981", fontSize: "18px" }}>₹{order.final_amount || order.amount || 0}</strong>
              </div>
            </div>
          </div>

          {/* ✅ REDESIGNED DELIVERY INFORMATION */}
          <div style={{ marginBottom: "20px" }}>
            <strong style={{ color: colors.textPrimary, fontSize: "16px", display: "block", marginBottom: "12px" }}>
              Delivery Information
            </strong>
            
            <div style={{
              background: colors.surface,
              borderRadius: "12px",
              border: `1px solid ${colors.border}`,
              overflow: "hidden"
            }}>
              {/* Pickup Address */}
              <div style={{
                padding: "16px 20px",
                borderBottom: `1px solid ${colors.borderLight}`,
                display: "flex",
                alignItems: "flex-start",
                gap: "12px"
              }}>
                <div style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  background: isDark ? "rgba(59,130,246,0.15)" : "rgba(59,130,246,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#3b82f6",
                  flexShrink: 0
                }}>
                  <LocationIcon />
                </div>
                <div>
                  <div style={{ fontSize: "11px", color: colors.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Pickup Address
                  </div>
                  <div style={{ fontSize: "14px", color: colors.textSecondary, marginTop: "4px" }}>
                    {order.pickup_address || "N/A"}
                  </div>
                </div>
              </div>

              {/* Delivery Address */}
              <div style={{
                padding: "16px 20px",
                borderBottom: `1px solid ${colors.borderLight}`,
                display: "flex",
                alignItems: "flex-start",
                gap: "12px"
              }}>
                <div style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  background: isDark ? "rgba(16,185,129,0.15)" : "rgba(16,185,129,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#10b981",
                  flexShrink: 0
                }}>
                  <LocationIcon />
                </div>
                <div>
                  <div style={{ fontSize: "11px", color: colors.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Delivery Address
                  </div>
                  <div style={{ fontSize: "14px", color: colors.textSecondary, marginTop: "4px" }}>
                    {order.dropoff_address || "N/A"}
                  </div>
                </div>
              </div>

              {/* ✅ SEPARATE RECEIVER DETAILS SECTION */}
              <div style={{
                padding: "20px 20px",
                borderBottom: `1px solid ${colors.borderLight}`,
                background: isDark ? "rgba(139,92,246,0.05)" : "rgba(139,92,246,0.03)"
              }}>
                <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "12px",
                  marginBottom: "12px"
                }}>
                  <div style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    flexShrink: 0
                  }}>
                    <UserIcon />
                  </div>
                  <div>
                    <div style={{ fontSize: "11px", color: colors.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Receiver Details
                    </div>
                  </div>
                </div>
                
                <div style={{ 
                  display: "flex", 
                  flexDirection: "column",
                  gap: "8px",
                  paddingLeft: "48px"
                }}>
                  {/* Receiver Name - Large and prominent */}
                  <div>
                    <div style={{ 
                      fontSize: "20px", 
                      fontWeight: 700, 
                      color: colors.textPrimary,
                      marginBottom: "2px"
                    }}>
                      {order.receiver_name || "N/A"}
                    </div>
                    <div style={{ 
                      fontSize: "11px", 
                      color: colors.textMuted,
                      fontWeight: 500
                    }}>
                      Receiver Name
                    </div>
                  </div>
                  
                  {/* Phone Number */}
                  <div>
                    <div style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: "8px",
                      fontSize: "16px",
                      fontWeight: 600,
                      color: colors.textPrimary
                    }}>
                      <PhoneIcon style={{ color: colors.textMuted }} />
                      {order.receiver_phone || "N/A"}
                    </div>
                    <div style={{ 
                      fontSize: "11px", 
                      color: colors.textMuted,
                      fontWeight: 500,
                      marginTop: "2px"
                    }}>
                      Phone Number
                    </div>
                  </div>
                </div>
              </div>

              {/* OTP Section */}
              {order.pickup_otp && (
                <div style={{
                  padding: "16px 20px",
                  background: isDark ? "rgba(245,158,11,0.05)" : "rgba(245,158,11,0.03)"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "11px", color: colors.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Pickup OTP
                    </span>
                    <span style={{
                      fontFamily: "monospace",
                      fontSize: "20px",
                      fontWeight: 700,
                      color: "#f59e0b",
                      background: isDark ? "rgba(245,158,11,0.15)" : "rgba(245,158,11,0.1)",
                      padding: "4px 16px",
                      borderRadius: "8px",
                      letterSpacing: "4px"
                    }}>
                      {order.pickup_otp}
                    </span>
                    {order.pickup_otp_verified_at && (
                      <span style={{
                        fontSize: "11px",
                        color: "#10b981",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px"
                      }}>
                        ✓ Verified at {formatDateTime(order.pickup_otp_verified_at)}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ✅ Delivery Images with Correct Paths */}
          {(pickupImage || dropoffImage) && (
            <div style={{ marginBottom: "20px" }}>
              <strong style={{ color: colors.textPrimary, fontSize: "16px", display: "block", marginBottom: "12px" }}>
                Delivery Images
              </strong>
              <div style={{ 
                display: "flex", 
                gap: "20px", 
                flexWrap: "wrap",
                background: colors.surface,
                padding: "16px",
                borderRadius: "12px",
                border: `1px solid ${colors.border}`
              }}>
                {pickupImage && (
                  <div>
                    <div style={{ fontSize: "11px", color: colors.textMuted, marginBottom: "6px", fontWeight: 600 }}>
                      📸 Pickup Photo
                    </div>
                    <img 
                      src={pickupImage}
                      alt="Pickup" 
                      style={{ 
                        width: "160px", 
                        height: "160px", 
                        borderRadius: "10px", 
                        objectFit: "cover", 
                        cursor: "pointer",
                        border: `2px solid ${colors.border}`,
                        transition: "transform 0.2s"
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.02)"}
                      onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                      onClick={() => window.open(pickupImage, '_blank')}
                      onError={(e) => {
                        e.target.style.display = "none";
                        const parent = e.target.parentElement;
                        const fallback = document.createElement('div');
                        fallback.style.cssText = `color: ${colors.textMuted}; font-size: 13px; padding: 10px; background: ${isDark ? '#1a2035' : '#f3f4f6'}; border-radius: 8px; text-align: center;`;
                        fallback.textContent = '⚠️ Image not available';
                        parent.appendChild(fallback);
                      }}
                    />
                  </div>
                )}
                {dropoffImage && (
                  <div>
                    <div style={{ fontSize: "11px", color: colors.textMuted, marginBottom: "6px", fontWeight: 600 }}>
                      📸 Delivery Photo
                    </div>
                    <img 
                      src={dropoffImage}
                      alt="Delivery" 
                      style={{ 
                        width: "160px", 
                        height: "160px", 
                        borderRadius: "10px", 
                        objectFit: "cover", 
                        cursor: "pointer",
                        border: `2px solid ${colors.border}`,
                        transition: "transform 0.2s"
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.02)"}
                      onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                      onClick={() => window.open(dropoffImage, '_blank')}
                      onError={(e) => {
                        e.target.style.display = "none";
                        const parent = e.target.parentElement;
                        const fallback = document.createElement('div');
                        fallback.style.cssText = `color: ${colors.textMuted}; font-size: 13px; padding: 10px; background: ${isDark ? '#1a2035' : '#f3f4f6'}; border-radius: 8px; text-align: center;`;
                        fallback.textContent = '⚠️ Image not available';
                        parent.appendChild(fallback);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Order Timeline */}
          <div style={{ marginBottom: "20px" }}>
            <strong style={{ color: colors.textPrimary, fontSize: "16px", display: "block", marginBottom: "12px" }}>
              Order Timeline
            </strong>
            <div style={{ 
              marginTop: "8px", 
              background: colors.surface, 
              padding: "20px", 
              borderRadius: "12px", 
              border: `1px solid ${colors.border}`
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", paddingBottom: "8px", borderBottom: `1px solid ${colors.border}` }}>
                <span style={{ color: colors.textMuted }}>Order Placed</span>
                <span style={{ color: colors.textSecondary }}>{formatDateTime(order.created_at)}</span>
              </div>
              {order.restro_accept_time && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", paddingBottom: "8px", borderBottom: `1px solid ${colors.border}` }}>
                  <span style={{ color: colors.textMuted }}>Restaurant Accepted</span>
                  <span style={{ color: colors.textSecondary }}>{formatDateTime(order.restro_accept_time)}</span>
                </div>
              )}
              {order.restro_ready_time && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", paddingBottom: "8px", borderBottom: `1px solid ${colors.border}` }}>
                  <span style={{ color: colors.textMuted }}>Restaurant Ready</span>
                  <span style={{ color: colors.textSecondary }}>{formatDateTime(order.restro_ready_time)}</span>
                </div>
              )}
              {order.rider_pickup_time && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", paddingBottom: "8px", borderBottom: `1px solid ${colors.border}` }}>
                  <span style={{ color: colors.textMuted }}>Rider Pickup</span>
                  <span style={{ color: colors.textSecondary }}>{formatDateTime(order.rider_pickup_time)}</span>
                </div>
              )}
              {order.order_delivered_time && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: colors.textMuted }}>Delivered</span>
                  <span style={{ color: "#10b981", fontWeight: 600 }}>{formatDateTime(order.order_delivered_time)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // If no user data, show error
  if (!user?.id) {
    return (
      <div style={{ 
        minHeight: "100vh", 
        background: colors.background, 
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px"
      }}>
        <div style={{
          background: colors.surface,
          padding: "40px",
          borderRadius: "16px",
          textAlign: "center",
          border: `1px solid ${colors.border}`
        }}>
          <div style={{ fontSize: "60px", marginBottom: "16px" }}>⚠️</div>
          <h2 style={{ color: colors.textPrimary }}>User Not Found</h2>
          <p style={{ color: colors.textMuted }}>No user data was provided. Please go back and select a user.</p>
          <button
            onClick={handleBack}
            style={{
              marginTop: "16px",
              padding: "8px 24px",
              background: "#3b82f6",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer"
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: "100vh", 
      background: colors.background, 
      fontFamily: "'DM Sans', sans-serif",
      padding: "20px 24px"
    }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .order-container {
          animation: slideIn 0.3s ease;
        }
      `}</style>

      <div className="order-container">
        {/* Header with Back Button */}
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: "16px", 
          marginBottom: "24px",
          flexWrap: "wrap"
        }}>
          <button 
            onClick={handleBack}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: "10px",
              cursor: "pointer",
              color: colors.textSecondary,
              fontSize: "13px",
              fontWeight: 500,
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isDark ? "#1e2740" : "#f3f4f6";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = colors.surface;
            }}
          >
            <ChevronLeftIcon />
            Back
          </button>
          
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0, fontSize: "24px", fontWeight: 700, color: colors.textPrimary }}>
              Order History
            </h1>
            <p style={{ margin: "8px 0 0", fontSize: "14px", color: colors.textMuted }}>
              {user?.name || 'User'} • {user?.contact || 'No Contact'} • User ID: {user?.id || 'N/A'}
            </p>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div style={{
            background: "rgba(239,68,68,0.1)",
            border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: "10px",
            padding: "12px 16px",
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <span style={{ fontSize: "13px", color: "#fca5a5" }}>⚠ {error}</span>
            <button 
              onClick={fetchOrders}
              style={{
                padding: "4px 12px",
                borderRadius: "6px",
                border: "none",
                background: colors.surface,
                color: colors.textPrimary,
                cursor: "pointer",
                fontSize: "12px"
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div style={{ 
            display: "flex", 
            flexDirection: "column",
            alignItems: "center", 
            justifyContent: "center", 
            padding: "80px 20px",
            background: colors.surface,
            borderRadius: "16px",
            border: `1px solid ${colors.border}`
          }}>
            <SpinnerIcon />
            <p style={{ marginTop: "16px", color: colors.textMuted }}>Loading orders...</p>
          </div>
        ) : (stats.total > 0) ? (
          <>
            {/* Total Orders Counter at Top */}
            <div style={{
              background: colors.surface,
              borderRadius: "16px",
              padding: "24px 20px",
              marginBottom: "24px",
              border: `1px solid ${colors.border}`,
              textAlign: "center"
            }}>
              <div style={{ fontSize: "14px", color: colors.textMuted, marginBottom: "8px" }}>Total Orders</div>
              <div style={{ fontSize: "48px", fontWeight: 800, color: "#3b82f6", lineHeight: 1 }}>{stats.total}</div>
              <div style={{ fontSize: "13px", color: colors.textMuted, marginTop: "8px" }}>
                Total Spent: <span style={{ fontWeight: 600, color: colors.textPrimary }}>₹{stats.totalSpent.toLocaleString()}</span>
              </div>
            </div>

            {/* Filter Tabs */}
            <div style={{
              display: "flex",
              gap: "12px",
              marginBottom: "20px",
              flexWrap: "wrap",
              borderBottom: `1px solid ${colors.border}`,
              paddingBottom: "12px"
            }}>
              {[
                { key: "all", label: "All Orders", count: stats.total },
                { key: "delivered", label: "Delivered", count: stats.delivered, color: "#10b981" },
                { key: "pending", label: "Pending", count: stats.pending, color: "#f59e0b" },
                { key: "cancelled", label: "Cancelled", count: stats.cancelled, color: "#ef4444" }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => filterOrders(tab.key)}
                  style={{
                    background: activeTab === tab.key ? (tab.color || "#3b82f6") : "transparent",
                    color: activeTab === tab.key ? "#fff" : colors.textSecondary,
                    border: activeTab === tab.key ? "none" : `1px solid ${colors.border}`,
                    padding: "8px 20px",
                    borderRadius: "24px",
                    fontSize: "13px",
                    fontWeight: 500,
                    cursor: "pointer",
                    transition: "all 0.2s"
                  }}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>

            {/* Orders Table */}
            <div style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: "12px",
              overflow: "hidden"
            }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1200px" }}>
                  <thead>
                    <tr style={{ background: isDark ? "#0f1520" : "#f9fafb" }}>
                      <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: isDark ? "#3b82f6" : "#4f46e5", borderBottom: `1px solid ${colors.border}` }}>#</th>
                      <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: isDark ? "#3b82f6" : "#4f46e5", borderBottom: `1px solid ${colors.border}` }}>Order ID</th>
                      <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: isDark ? "#3b82f6" : "#4f46e5", borderBottom: `1px solid ${colors.border}` }}>Date & Time</th>
                      <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: isDark ? "#3b82f6" : "#4f46e5", borderBottom: `1px solid ${colors.border}` }}>Restaurant</th>
                      <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: isDark ? "#3b82f6" : "#4f46e5", borderBottom: `1px solid ${colors.border}` }}>Items</th>
                      <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: isDark ? "#3b82f6" : "#4f46e5", borderBottom: `1px solid ${colors.border}` }}>Amount</th>
                      <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: isDark ? "#3b82f6" : "#4f46e5", borderBottom: `1px solid ${colors.border}` }}>Payment</th>
                      <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: isDark ? "#3b82f6" : "#4f46e5", borderBottom: `1px solid ${colors.border}` }}>Status</th>
                      <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: isDark ? "#3b82f6" : "#4f46e5", borderBottom: `1px solid ${colors.border}` }}>Delivery Image</th>
                      <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: isDark ? "#3b82f6" : "#4f46e5", borderBottom: `1px solid ${colors.border}` }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order, idx) => {
                      const sn = stats.total - idx;
                      const restaurantName = getRestaurantName(order);
                      const pickupImage = getImageUrl(order.delivery_pickup_pic);
                      const dropoffImage = getImageUrl(order.delivery_dropoff_pic);
                      
                      return (
                        <tr
                          key={order.id || idx}
                          style={{
                            borderBottom: idx < orders.length - 1 ? `1px solid ${colors.borderLight}` : "none",
                            transition: "background 0.2s ease"
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)")
                          }
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <td style={{ padding: "12px 14px", fontSize: "13px", fontWeight: 600, color: colors.textPrimary }}>
                            {sn}
                          </td>
                          <td style={{ padding: "12px 14px", fontSize: "13px", fontWeight: 600, color: colors.textPrimary, fontFamily: "monospace" }}>
                            #{order.id}
                          </td>
                          <td style={{ padding: "12px 14px", fontSize: "12px", color: colors.textMuted }}>
                            {formatDateTime(order.created_at)}
                          </td>
                          <td style={{ padding: "12px 14px", fontSize: "12px", color: colors.textSecondary }}>
                            {restaurantName}
                          </td>
                          <td style={{ padding: "12px 14px", fontSize: "12px", color: colors.textSecondary }}>
                            {getItemsList(order.items)}
                          </td>
                          <td style={{ padding: "12px 14px", fontSize: "14px", fontWeight: 700, color: colors.textPrimary }}>
                            ₹{order.final_amount || order.amount || 0}
                          </td>
                          <td style={{ padding: "12px 14px", fontSize: "12px", color: colors.textSecondary }}>
                            <span style={{
                              display: "inline-block",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              background: order.pay_method === "COD" ? "rgba(245,158,11,0.1)" : "rgba(16,185,129,0.1)",
                              color: order.pay_method === "COD" ? "#f59e0b" : "#10b981"
                            }}>
                              {order.pay_method || "N/A"}
                            </span>
                          </td>
                          <td style={{ padding: "12px 14px" }}>
                            <OrderStatusBadge status={order.order_status_user || order.order_status_restro || order.status} theme={theme} />
                          </td>
                          <td style={{ padding: "12px 14px" }}>
                            {dropoffImage ? (
                              <img 
                                src={dropoffImage}
                                alt="Delivery" 
                                style={{ 
                                  width: "40px", 
                                  height: "40px", 
                                  borderRadius: "6px", 
                                  objectFit: "cover",
                                  cursor: "pointer",
                                  border: `1px solid ${colors.border}`
                                }}
                                onClick={() => window.open(dropoffImage, '_blank')}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : pickupImage ? (
                              <img 
                                src={pickupImage}
                                alt="Pickup" 
                                style={{ 
                                  width: "40px", 
                                  height: "40px", 
                                  borderRadius: "6px", 
                                  objectFit: "cover",
                                  cursor: "pointer",
                                  border: `1px solid ${colors.border}`
                                }}
                                onClick={() => window.open(pickupImage, '_blank')}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <span style={{ fontSize: "10px", color: colors.textMuted }}>No image</span>
                            )}
                          </td>
                          <td style={{ padding: "12px 14px" }}>
                            <button
                              onClick={() => setSelectedOrder(order)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: "#3b82f6",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                fontSize: "12px",
                                padding: "4px 8px",
                                borderRadius: "6px",
                                transition: "all 0.2s"
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = "rgba(59,130,246,0.1)"}
                              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                            >
                              <EyeIcon /> View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div style={{
            textAlign: "center",
            padding: "80px 20px",
            background: colors.surface,
            borderRadius: "16px",
            border: `1px solid ${colors.border}`
          }}>
            <div style={{ fontSize: "80px", marginBottom: "20px" }}>🛒</div>
            <h3 style={{ margin: "0 0 12px", color: colors.textPrimary, fontSize: "20px" }}>No Orders Found</h3>
            <p style={{ margin: 0, fontSize: "14px", color: colors.textMuted }}>
              This user hasn't placed any orders yet
            </p>
          </div>
        )}
      </div>

      {/* Full Page Order Detail View */}
      {selectedOrder && (
        <OrderDetailPage order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}
    </div>
  );
};

export default OrderHistory;